// The library: every file, filterable by phase, type and deliverable status, with an
// "awaiting client review" view and search over titles and tags.
import { useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { docTypeLabel, useDocuments, type DocType } from '@/lib/documents';
import { useTracker } from '@/lib/tracker';
import type { Program } from '@/lib/types';
import { Input, Select } from '@/components/ui/field';
import { Card, PageHeader } from '@/components/ui/misc';
import { DocumentList } from '@/components/documents/DocumentList';
import { UploadDialog } from '@/components/documents/UploadDialog';
import { Button } from '@/components/ui/button';
import { Upload } from 'lucide-react';

const TYPES: DocType[] = [
  'DELIVERABLE',
  'WORKING_PAPER',
  'CLIENT_PROVIDED',
  'VENDOR_MATERIAL',
  'MEETING_RECORD',
  'PUBLISHED_REPORT',
  'INTERNAL',
];

export function DocumentsPage() {
  const program = useOutletContext<Program>();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const [uploading, setUploading] = useState(false);
  const tracker = useTracker(program.id);
  const isAdmin = program.capabilities.includes('plan.manage');
  const internal = program.capabilities.includes('documents.internal');
  const phaseId = params.get('phase') ?? '';
  const type = params.get('type') ?? '';
  const awaiting = params.get('awaiting') === '1';
  const docs = useDocuments(program.id, {
    type: type || undefined,
    q: params.get('q') || undefined,
    awaiting: awaiting || undefined,
  });

  const set = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  // Phase filter covers files on the phase, its tasks and its deliverables.
  const data = tracker.data;
  const inPhase = (d: { phaseId: string | null; taskId: string | null; deliverableId: string | null }) =>
    !phaseId ||
    d.phaseId === phaseId ||
    (d.taskId && data?.tasks.find((t) => t.id === d.taskId)?.phaseId === phaseId) ||
    (d.deliverableId && data?.deliverables.find((x) => x.id === d.deliverableId)?.phaseId === phaseId);
  const shown = docs.data?.filter(inPhase);

  const context = (d: { taskId: string | null; deliverableId: string | null; phaseId: string | null }) => {
    if (d.deliverableId)
      return ` · deliverable: ${data?.deliverables.find((x) => x.id === d.deliverableId)?.name ?? ''}`;
    if (d.taskId) return ` · task: ${data?.tasks.find((x) => x.id === d.taskId)?.title ?? ''}`;
    if (d.phaseId) return ` · Phase ${data?.phases.find((x) => x.id === d.phaseId)?.number ?? ''}`;
    return '';
  };

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-5">
      <PageHeader
        title="Documents"
        description="Every file, tied to its phase, task or deliverable. New uploads become versions; every version is kept."
        actions={
          isAdmin && (
            <Button onClick={() => setUploading(true)}>
              <Upload /> File a document
            </Button>
          )
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form
          className="relative"
          onSubmit={(e) => {
            e.preventDefault();
            set('q', q.trim() || null);
          }}
        >
          <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-subtle" />
          <Input
            aria-label="Search titles and tags"
            placeholder="Search titles and tags"
            className="h-9 w-64 pl-8"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onBlur={() => set('q', q.trim() || null)}
          />
        </form>
        <Select
          aria-label="Phase"
          className="h-9 w-56 text-[13px]"
          value={phaseId}
          onChange={(e) => set('phase', e.target.value || null)}
        >
          <option value="">All phases</option>
          {data?.phases.map((p) => (
            <option key={p.id} value={p.id}>
              {p.number}. {p.name}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Type"
          className="h-9 w-44 text-[13px]"
          value={type}
          onChange={(e) => set('type', e.target.value || null)}
        >
          <option value="">All types</option>
          {TYPES.filter((t) => internal || t !== 'INTERNAL').map((t) => (
            <option key={t} value={t}>
              {docTypeLabel(t, program)}
            </option>
          ))}
        </Select>
        <label className="flex items-center gap-1.5 text-[13px] text-muted">
          <input
            type="checkbox"
            className="size-4 accent-[var(--brand)]"
            checked={awaiting}
            onChange={(e) => set('awaiting', e.target.checked ? '1' : null)}
          />
          Awaiting {program.clientShortName} review
        </label>
        <span className="ml-auto text-xs text-muted tabular">{shown?.length ?? 0} documents</span>
      </div>
      <Card className="px-4 py-1">
        <DocumentList
          program={program}
          docs={shown}
          loading={docs.isLoading}
          emptyText="No documents match."
          showContext={context}
        />
      </Card>
      <p className="mt-3 text-xs text-muted">
        To file against a task or deliverable, drop the file on it in the{' '}
        <Link to={`/p/${program.id}/tracker`} className="underline">
          Tracker
        </Link>{' '}
        or the{' '}
        <Link to={`/p/${program.id}/deliverables`} className="underline">
          deliverable register
        </Link>
        .
      </p>
      <UploadDialog
        program={program}
        target={{ label: 'the program library' }}
        open={uploading}
        onClose={() => setUploading(false)}
      />
    </div>
  );
}
